import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  FiSearch,
  FiPlus,
  FiChevronLeft,
  FiChevronRight,
  FiClipboard,
  FiCalendar,
  FiUser,
  FiCheckCircle,
  FiAlertCircle
} from "react-icons/fi";
import { FaBoxes, FaStore } from "react-icons/fa";

const StockOpnamePage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  const [stockList, setStockList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [loading, setLoading] = useState(true);

  const [role, setRole] = useState("");
  const [selectedOutlet, setSelectedOutlet] = useState(
    localStorage.getItem("id_outlet") || ""
  );
  const [outlets, setOutlets] = useState([]);

  const fetchOutlets = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v1/outlet`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const rawOutlets = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data?.data?.data)
        ? res.data.data.data
        : [];
      setOutlets(rawOutlets);
      if (rawOutlets.length > 0 && !selectedOutlet) {
        setSelectedOutlet(rawOutlets[0].id);
      }
    } catch (err) {
      console.error("Gagal mengambil data outlet:", err);
    }
  };

  const fetchStockOpname = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        search: searchTerm,
        order_by: "created_at",
        sort: "desc",
      };

      if (selectedOutlet) {
        params.outlet_id = selectedOutlet;
      }

      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v1/stock-opname`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        }
      );

      const result = res.data;
      const rawData = result.data || [];
      setStockList(rawData);
      setLastPage(result.meta?.last_page || 1);
      setTotalData(result.meta?.total || rawData.length);
    } catch (error) {
      console.error("Gagal fetch data stock opname:", error);
      setStockList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const userRole = (localStorage.getItem("role") || "").toLowerCase();
    setRole(userRole);

    if (["owner", "admin", "superadmin"].includes(userRole)) {
      fetchOutlets();
    } else {
      setSelectedOutlet(localStorage.getItem("id_outlet"));
    }
  }, []);

  useEffect(() => {
    fetchStockOpname();
  }, [page, searchTerm, selectedOutlet]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  return (
    <div className="space-y-6 pb-12">
      {/* 1. TOP METRIC SUMMARY BAR */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-5"
      >
        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Total Stock Opname
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900">
              {loading ? "..." : totalData}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <FaBoxes className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Opname Halaman Ini
            </p>
            <h2 className="text-3xl font-extrabold text-teal-600">
              {loading ? "..." : stockList.length}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-teal-50 text-teal-600 border border-teal-100">
            <FiCheckCircle className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Outlet Aktif
            </p>
            <h2 className="text-3xl font-extrabold text-amber-600 truncate max-w-[160px]">
              {selectedOutlet
                ? outlets.find((o) => String(o.id) === String(selectedOutlet))?.name || "Outlet #" + selectedOutlet
                : "Semua Outlet"}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-amber-50 text-amber-600 border border-amber-100">
            <FaStore className="w-6 h-6" />
          </div>
        </div>
      </motion.div>

      {/* 2. MAIN CONTROLS & TABLE CARD */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white p-6 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6"
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Live Search & Outlet Selector */}
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <div className="relative w-full md:w-72">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Cari kode opname, outlet, pegawai..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-semibold"
              />
            </div>

            {/* Outlet Selector (Owner/Admin) */}
            {["owner", "admin", "superadmin"].includes(role) && (
              <select
                value={selectedOutlet || ""}
                onChange={(e) => {
                  setSelectedOutlet(e.target.value);
                  setPage(1);
                }}
                className="p-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              >
                <option value="">-- Pilih Outlet --</option>
                {outlets.map((outlet) => (
                  <option key={outlet.id} value={outlet.id}>
                    {outlet.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Create CTA Button (GREEN BUTTON) */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate("/inventory/stock-opname/create")}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold rounded-2xl shadow-lg shadow-emerald-600/20 transition-all"
          >
            <FiPlus className="w-4 h-4" />
            <span>Tambah Stock Opname</span>
          </motion.button>
        </div>

        {/* TABLE CONTENT */}
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : stockList.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">
            <FaBoxes className="w-12 h-12 mx-auto mb-2 text-gray-300 stroke-1" />
            <p className="font-semibold text-gray-600">Belum ada riwayat stock opname</p>
            <p className="text-xs text-gray-400 mt-1">
              Klik tombol 'Tambah Stock Opname' untuk melakukan penyesuaian stok fisik obat.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
            <table className="w-full text-left text-xs text-gray-600">
              <thead>
                <tr className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                  <th className="p-3.5 w-12 text-center">No</th>
                  <th className="p-3.5">Kode Opname</th>
                  <th className="p-3.5">Outlet</th>
                  <th className="p-3.5">Pegawai Penanggung Jawab</th>
                  <th className="p-3.5">Tanggal Dibuat</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {stockList.map((item, idx) => (
                  <tr key={item.id} className="hover:bg-emerald-50/20 transition-colors">
                    <td className="p-3.5 text-center font-semibold text-gray-400">
                      {(page - 1) * 10 + idx + 1}
                    </td>
                    <td className="p-3.5 font-bold text-gray-900">
                      <span className="inline-flex px-3 py-1 rounded-full text-xs font-mono font-bold bg-emerald-50 text-emerald-700 border border-emerald-100">
                        {item.code || `SO-#${item.id}`}
                      </span>
                    </td>
                    <td className="p-3.5 font-semibold text-gray-800 flex items-center gap-1.5 pt-4">
                      <FaStore className="text-emerald-600 w-3.5 h-3.5" />
                      <span>{item.outlet?.name || item.outlet_name || "Outlet Pusat"}</span>
                    </td>
                    <td className="p-3.5 font-semibold text-gray-700">
                      <div className="flex items-center gap-1.5">
                        <FiUser className="text-teal-600 w-3.5 h-3.5" />
                        <span>{item.employee?.name || item.employee_name || "Petugas Apotek"}</span>
                      </div>
                    </td>
                    <td className="p-3.5 font-semibold text-gray-600">
                      <div className="flex items-center gap-1.5">
                        <FiCalendar className="text-gray-400 w-3.5 h-3.5" />
                        <span>
                          {item.created_at
                            ? new Date(item.created_at).toLocaleString("id-ID", {
                                dateStyle: "medium",
                                timeStyle: "short",
                              })
                            : "-"}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* PAGINATION CONTROLS */}
        <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t border-gray-100 gap-3 text-xs">
          <p className="text-gray-400">
            Menampilkan <span className="font-bold text-gray-700">{stockList.length}</span> dari{" "}
            <span className="font-bold text-gray-700">{totalData}</span> opname
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page === 1}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                page === 1
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs font-semibold text-gray-600 px-2">
              Halaman {page} dari {lastPage}
            </span>
            <button
              onClick={() => setPage((prev) => Math.min(prev + 1, lastPage))}
              disabled={page === lastPage}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                page === lastPage
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default StockOpnamePage;
